// Page docs (structure + permissions + flows): pages/home/README.md
import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import Head from "next/head";
import { Box, Container, Typography, Card, CardMedia, CardContent, Chip, Skeleton, Button, IconButton } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { styled } from "@mui/material/styles";
import { useRouter } from "next/router";
import NavBar from "@/components/Importable/Navigation";
import SectionFooter from "@/components/CreateForm/Footer";
import SubscriptionBanner from "@/components/Banners/SubscriptionBanner";
import NewsCarousel from "@/components/Banners/NewsCarousel";
import { useGetPromotionsQuery, useGetArticlesQuery, useGetEventsQuery, useGetRegulationsQuery } from "@/src/services/api";
import { useGetOrganizationsQuery } from "@/src/services/organizationsApi";
import { assetUrl } from "@/utils/asset";
import { purple, blue, green, common } from "@/theme/colors";
import { FeaturedOrganizationSection } from "@/components/OrganizationSections/FeaturedOrganization";
import { FeaturedProfessionalsSection, mapProfessionalToCard } from "@/pages/professionals/index";
import { useGetProfessionalsQuery } from "@/src/services/api";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { SingleFeaturedBanner, SingleFeaturedBannerSkeleton } from "@/components/Importable/FeaturedBanner";
import { isEventPast } from "@/components/Importable/Cards";

// Stable identity for "no organizations yet": `organizations` is undefined
// whenever its query is skipped (the default), so `organizations || []`
// would be a fresh array every render, resetting the featured banner's
// carousel effects mid-animation (a flicker source).
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const EMPTY_ORGS = [];

// ── styled ───────────────────────────────────────────────────────────────────

const PageWrapper = styled(Box)({
  minHeight: "100vh",
  backgroundColor: "#fafbff"
});
const Section = styled(Box)(({
  theme
}) => ({
  padding: theme.spacing(6, 0),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(4, 0)
  }
}));
const SectionTitle = styled(Typography)(({
  theme
}) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(3),
  fontSize: "1.75rem",
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.35rem"
  }
}));
const ArticleCard = styled(Card)(({
  theme
}) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  borderRadius: 12,
  overflow: "hidden",
  transition: "transform 0.2s, box-shadow 0.2s",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[6]
  }
}));

// ── helpers ──────────────────────────────────────────────────────────────────

const stockImages = ["/img/bg.jpg", "/img/bg0.jpg", "/img/bg2.jpg", "/img/bg3.jpg", "/img/bg4.jpg", "/img/bg5.jpg", "/img/bg6.jpg", "/img/bg8.jpg", "/img/bg9.jpg", "/img/bg10.jpg"];
function pickImage(index) {
  return stockImages[index % stockImages.length];
}
function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}
function formatShortDate(iso) {
  const d = new Date(iso);
  return {
    month: d.toLocaleDateString("en-US", {
      month: "short"
    }).toUpperCase(),
    day: String(d.getDate())
  };
}

/** Build a NewsItem from a promoted article entity. */
function articleToNewsItem(art, imageUrl) {
  const p = art.payload || {};
  const tags = [...(Array.isArray(p.regions) ? p.regions : []), ...(Array.isArray(p.tags) ? p.tags : [])];
  return {
    tag: tags[0] || "Article",
    date: formatDate(art.approvedAt || art.createdAt),
    title: p.title || art.title || "Untitled",
    subtitle: p.executiveSummary || art.description || "",
    ctaUrl: `/articles/${art.id || p.id || art.slug}`,
    backgroundImage: imageUrl || p.image || pickImage(0),
    ctaColor: purple.primary
  };
}

/** Build a NewsItem from a promoted regulation entity. */
function regulationToNewsItem(reg, imageUrl) {
  const p = reg.payload || {};
  const tags = [...(Array.isArray(p.regions) ? p.regions : []), ...(Array.isArray(p.tags) ? p.tags : [])];
  return {
    tag: tags[0] || "Regulation",
    date: formatDate(reg.approvedAt || reg.createdAt),
    title: p.title || reg.title || "Untitled",
    subtitle: p.executiveSummary || reg.description || "",
    ctaUrl: `/regulations/${reg.id || p.id || reg.slug}`,
    backgroundImage: imageUrl || p.image || pickImage(2),
    ctaColor: blue.primary
  };
}

/** Check if an event is within the next 4 weeks. */
function isUpcomingSoon(evt) {
  const p = evt.payload || {};
  const dateStr = p.date || evt.createdAt;
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const fourWeeksOut = new Date(now.getTime() + 28 * 24 * 60 * 60 * 1000);
  return d >= now && d <= fourWeeksOut;
}

// ── Section Components ───────────────────────────────────────────────────────

function NewsSection({
  newsItems
}) {
  if (newsItems.length === 0) return null;
  return /*#__PURE__*/_jsx(Box, {
    "data-select-target": "News Carousel",
    sx: {
      pt: 0
    },
    children: /*#__PURE__*/_jsx(NewsCarousel, {
      items: newsItems
    })
  });
}
function ServicesSection({
  hasNews
}) {
  const router = useRouter();
  return /*#__PURE__*/_jsx(Box, {
    "data-select-target": "Services Banner",
    sx: {
      pt: hasNews ? {
        xs: 5,
        md: 7
      } : {
        xs: 5,
        md: 7
      },
      pb: {
        xs: 5,
        md: 7
      },
      position: "relative",
      backgroundImage: `url('${assetUrl("/PlaceholderImgs/LabBanner.jpeg")}')`,
      backgroundPosition: "center center",
      backgroundSize: "cover",
      color: common.white,
      textAlign: "center",
      px: 2,
      "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        backgroundColor: `rgba(100,100,100,0.5)`
      }
    },
    children: /*#__PURE__*/_jsxs(Container, {
      maxWidth: "md",
      sx: {
        position: "relative",
        zIndex: 1
      },
      children: [/*#__PURE__*/_jsx(Typography, {
        variant: "h3",
        component: "h1",
        sx: {
          fontWeight: 800,
          mb: 2,
          fontSize: {
            xs: "1.75rem",
            sm: "2.25rem",
            md: "3rem"
          }
        },
        children: "Product Certification Services"
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "body1",
        sx: {
          mb: 3,
          maxWidth: 680,
          mx: "auto",
          opacity: 0.92,
          fontSize: {
            xs: "0.95rem",
            md: "1.1rem"
          },
          lineHeight: 1.6
        },
        children: "Navigate the complex world of global product compliance with our extensive directory of certification organizations, regulatory updates, and industry resources."
      }), /*#__PURE__*/_jsx(Button, {
        variant: "contained",
        size: "large",
        onClick: () => router.push("/organizations"),
        sx: {
          backgroundColor: common.white,
          color: green.primary,
          fontWeight: 700,
          px: 4,
          "&:hover": {
            backgroundColor: "#e8e8e8"
          }
        },
        children: "Search Directory"
      })]
    })
  });
}
function LatestUpdatesSection({
  latestUpdates,
  loading,
  hasNews
}) {
  const router = useRouter();
  // Rules of Hooks: this early-out must come AFTER every hook below, or
  // the hook count changes between renders (crash: "rendered more hooks
  // than during the previous render") when latestUpdates populates.
  const hideSection = !loading && latestUpdates.length === 0;
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);
  useEffect(() => {
    updateScrollButtons();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollButtons, {
      passive: true
    });
    const ro = new ResizeObserver(updateScrollButtons);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollButtons);
      ro.disconnect();
    };
  }, [updateScrollButtons, latestUpdates, loading]);
  const scroll = direction => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = 280 + 24; // card width + gap
    const visible = Math.max(1, Math.floor(el.clientWidth / cardWidth));
    const delta = visible * cardWidth;
    el.scrollBy({
      left: direction === "left" ? -delta : delta,
      behavior: "smooth"
    });
  };
  if (hideSection) return null;
  return /*#__PURE__*/_jsxs(Section, {
    "data-select-target": "Industry Updates",
    sx: {
      pb: {
        xs: 1,
        sm: 2
      },
      pt: hasNews ? undefined : {
        xs: 5,
        md: 7
      }
    },
    children: [/*#__PURE__*/_jsx(Container, {
      maxWidth: "lg",
      children: /*#__PURE__*/_jsxs(Box, {
        sx: {
          display: "flex",
          alignItems: "center",
          mb: 3
        },
        children: [/*#__PURE__*/_jsx(SectionTitle, {
          sx: {
            mb: 0
          },
          children: "Latest Industry Updates"
        }), /*#__PURE__*/_jsxs(Box, {
          sx: {
            ml: 1.5,
            display: "flex",
            gap: 0.5
          },
          children: [canScrollLeft && /*#__PURE__*/_jsx(IconButton, {
            size: "small",
            "aria-label": "Scroll left",
            onClick: () => scroll("left"),
            sx: {
              bgcolor: "background.paper",
              boxShadow: 1,
              "&:hover": {
                bgcolor: "action.hover"
              }
            },
            children: /*#__PURE__*/_jsx(ArrowBackIosNewIcon, {
              fontSize: "small"
            })
          }), canScrollRight && /*#__PURE__*/_jsx(IconButton, {
            size: "small",
            "aria-label": "Scroll right",
            onClick: () => scroll("right"),
            sx: {
              bgcolor: "background.paper",
              boxShadow: 1,
              "&:hover": {
                bgcolor: "action.hover"
              }
            },
            children: /*#__PURE__*/_jsx(ArrowForwardIosIcon, {
              fontSize: "small"
            })
          })]
        })]
      })
    }), /*#__PURE__*/_jsx(Box, {
      ref: scrollRef,
      sx: {
        display: "flex",
        gap: 3,
        overflowX: "auto",
        px: "20px",
        scrollPaddingLeft: "20px",
        pb: 1,
        scrollSnapType: "x mandatory",
        "&::-webkit-scrollbar": {
          height: 6
        },
        "&::-webkit-scrollbar-thumb": {
          borderRadius: 3,
          backgroundColor: "rgba(0,0,0,0.15)"
        }
      },
      children: loading ? Array.from({
        length: 6
      }).map((_, i) => /*#__PURE__*/_jsx(Box, {
        sx: {
          flex: "0 0 280px",
          scrollSnapAlign: "start"
        },
        children: /*#__PURE__*/_jsx(Skeleton, {
          variant: "rectangular",
          height: 260,
          sx: {
            borderRadius: 3,
            width: 280
          }
        })
      }, i)) : latestUpdates.map((item, idx) => {
        const p = item.data.payload || {};
        const isReg = item.type === "regulation";
        const route = isReg ? `/regulations/${item.data.id || p.id || item.data.slug}` : `/articles/${item.data.id || p.id || item.data.slug}`;
        const tags = [...(Array.isArray(p.regions) ? p.regions : []), ...(Array.isArray(p.regionTags) ? p.regionTags : []), ...(Array.isArray(p.tags) ? p.tags : []), ...(Array.isArray(p.filterTags) ? p.filterTags : [])];
        const uniqueTags = Array.from(new Set(tags));
        return /*#__PURE__*/_jsx(Box, {
          sx: {
            flex: "0 0 280px",
            scrollSnapAlign: "start"
          },
          children: /*#__PURE__*/_jsxs(ArticleCard, {
            onClick: () => router.push(route),
            sx: {
              height: "100%"
            },
            children: [/*#__PURE__*/_jsx(CardMedia, {
              component: "img",
              loading: "lazy",
              image: assetUrl(p.image || pickImage(idx)),
              alt: p.title || item.data.title || "",
              sx: {
                height: 180,
                objectFit: "cover",
                objectPosition: "center"
              }
            }), /*#__PURE__*/_jsxs(CardContent, {
              sx: {
                flexGrow: 1
              },
              children: [/*#__PURE__*/_jsxs(Box, {
                sx: {
                  display: "flex",
                  gap: 0.5,
                  flexWrap: "wrap",
                  mb: 1
                },
                children: [/*#__PURE__*/_jsx(Chip, {
                  label: isReg ? p.type === "article" ? "Article" : "Regulatory Brief" : "Article",
                  size: "small",
                  sx: {
                    fontSize: "0.7rem",
                    height: 22,
                    backgroundColor: isReg ? blue.primary : purple.primary,
                    color: common.white
                  }
                }), uniqueTags.slice(0, 2).map(t => /*#__PURE__*/_jsx(Chip, {
                  label: t,
                  size: "small",
                  sx: {
                    fontSize: "0.7rem",
                    height: 22
                  }
                }, t))]
              }), /*#__PURE__*/_jsx(Typography, {
                variant: "subtitle1",
                fontWeight: 600,
                gutterBottom: true,
                sx: {
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden"
                },
                children: p.title || item.data.title || "Untitled"
              }), /*#__PURE__*/_jsx(Typography, {
                variant: "caption",
                color: "text.secondary",
                children: formatDate(item.data.approvedAt || item.data.createdAt)
              })]
            })]
          })
        }, item.data.id);
      })
    })]
  });
}
function FeaturedOrgsSection({
  organizations
}) {
  const router = useRouter();
  return /*#__PURE__*/_jsx(Section, {
    "data-select-target": "Featured Organizations",
    sx: {
      pt: {
        xs: 1,
        sm: 2
      },
      pb: {
        xs: 1,
        sm: 2
      }
    },
    children: /*#__PURE__*/_jsx(Container, {
      maxWidth: "lg",
      children: /*#__PURE__*/_jsx(FeaturedOrganizationSection, {
        orgs: organizations,
        onVisit: o => router.push(`/organizations/${o.id || o.slug}`)
      })
    })
  });
}
function FeaturedProfilesHomeSection() {
  const router = useRouter();
  const {
    data: professionals
  } = useGetProfessionalsQuery();
  const cards = useMemo(() => (professionals ?? []).filter(p => p.isActive && p.approvalStatus === "approved").map(mapProfessionalToCard), [professionals]);
  return /*#__PURE__*/_jsx(Section, {
    "data-select-target": "Featured Profiles",
    sx: {
      pt: {
        xs: 1,
        sm: 2
      },
      pb: {
        xs: 1,
        sm: 2
      }
    },
    children: /*#__PURE__*/_jsx(Container, {
      maxWidth: "lg",
      children: /*#__PURE__*/_jsx(FeaturedProfessionalsSection, {
        professionals: cards,
        onVisit: pro => router.push(`/professionals/${pro.slug || pro.id}`)
      })
    })
  });
}
function FeaturedEventsSection({
  featuredEvts,
  loading
}) {
  const router = useRouter();
  // Memoized so a parent re-render doesn't hand the banner a new array
  // identity and reset its cross-fade mid-animation (flicker source).
  const items = useMemo(() => featuredEvts.map((evt, idx) => {
    const p = evt.payload || {};
    return {
      id: evt.id,
      image: assetUrl(p.image || pickImage(idx + 3)),
      name: p.header || evt.title,
      description: p.description || evt.description || "",
      link: `/events/${evt.id || evt.slug}`,
      price: p.price,
      currency: p.currency
    };
  }), [featuredEvts]);
  if (!loading && featuredEvts.length === 0) return null;
  return /*#__PURE__*/_jsx(Section, {
    "data-select-target": "Featured Events",
    sx: {
      pt: {
        xs: 1,
        sm: 1
      },
      pb: {
        xs: 1,
        sm: 1
      }
    },
    children: /*#__PURE__*/_jsx(Container, {
      maxWidth: "lg",
      children: loading ? /*#__PURE__*/_jsx(SingleFeaturedBannerSkeleton, {
        accentColor: green.primary
      }) : /*#__PURE__*/_jsx(SingleFeaturedBanner, {
        items: items,
        accentColor: green.primary,
        accentDark: green.dark,
        ctaLabel: "View Event",
        sectionLabel: "Featured Event",
        onCta: item => router.push(item.link),
        dataSelectTarget: "Featured Events",
        intervalMs: 60000
      })
    })
  });
}
function UpcomingEventsSection({
  upcomingEvents
}) {
  const router = useRouter();
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);
  useEffect(() => {
    updateScrollButtons();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollButtons, {
      passive: true
    });
    const ro = new ResizeObserver(updateScrollButtons);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollButtons);
      ro.disconnect();
    };
  }, [updateScrollButtons, upcomingEvents]);
  const scroll = direction => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = 300 + 24; // card min-width + gap
    const visible = Math.max(1, Math.floor(el.clientWidth / cardWidth));
    const delta = visible * cardWidth;
    el.scrollBy({
      left: direction === "left" ? -delta : delta,
      behavior: "smooth"
    });
  };
  if (upcomingEvents.length === 0) return null;
  return /*#__PURE__*/_jsx(Section, {
    "data-select-target": "Upcoming Events",
    sx: {
      pt: {
        xs: 0.5,
        sm: 1
      }
    },
    children: /*#__PURE__*/_jsxs(Container, {
      maxWidth: "lg",
      children: [/*#__PURE__*/_jsxs(Box, {
        sx: {
          display: "flex",
          alignItems: "center",
          mb: 3
        },
        children: [/*#__PURE__*/_jsx(SectionTitle, {
          sx: {
            mb: 0
          },
          children: "Upcoming Events"
        }), /*#__PURE__*/_jsxs(Box, {
          sx: {
            ml: 1.5,
            display: "flex",
            gap: 0.5
          },
          children: [canScrollLeft && /*#__PURE__*/_jsx(IconButton, {
            size: "small",
            "aria-label": "Scroll left",
            onClick: () => scroll("left"),
            sx: {
              bgcolor: "background.paper",
              boxShadow: 1,
              "&:hover": {
                bgcolor: "action.hover"
              }
            },
            children: /*#__PURE__*/_jsx(ArrowBackIosNewIcon, {
              fontSize: "small"
            })
          }), canScrollRight && /*#__PURE__*/_jsx(IconButton, {
            size: "small",
            "aria-label": "Scroll right",
            onClick: () => scroll("right"),
            sx: {
              bgcolor: "background.paper",
              boxShadow: 1,
              "&:hover": {
                bgcolor: "action.hover"
              }
            },
            children: /*#__PURE__*/_jsx(ArrowForwardIosIcon, {
              fontSize: "small"
            })
          })]
        })]
      }), /*#__PURE__*/_jsx(Box, {
        ref: scrollRef,
        sx: {
          display: "flex",
          gap: 3,
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          pb: 1,
          mx: -1,
          px: 1,
          "&::-webkit-scrollbar": {
            height: 6
          },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "action.disabled",
            borderRadius: 3
          }
        },
        children: upcomingEvents.map((evt, idx) => {
          const p = evt.payload || {};
          const dateStr = p.date || evt.createdAt;
          const {
            month,
            day
          } = formatShortDate(dateStr);
          return /*#__PURE__*/_jsx(Box, {
            sx: {
              minWidth: 300,
              maxWidth: 340,
              flexShrink: 0,
              scrollSnapAlign: "start"
            },
            children: /*#__PURE__*/_jsxs(ArticleCard, {
              onClick: () => router.push(`/events/${evt.id || evt.slug}`),
              children: [/*#__PURE__*/_jsx(CardMedia, {
                component: "img",
                loading: "lazy",
                image: assetUrl(p.image || pickImage(idx + 6)),
                alt: p.header || evt.title || "",
                sx: {
                  height: 180,
                  objectFit: "cover",
                  objectPosition: "center"
                }
              }), /*#__PURE__*/_jsxs(CardContent, {
                sx: {
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column"
                },
                children: [/*#__PURE__*/_jsxs(Box, {
                  sx: {
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1
                  },
                  children: [/*#__PURE__*/_jsx(Chip, {
                    label: `${month} ${day}`,
                    size: "small",
                    sx: {
                      fontSize: "0.7rem",
                      height: 22,
                      backgroundColor: green.primary,
                      color: common.white,
                      fontWeight: 700
                    }
                  }), (p.location?.city || p.isVirtual) && /*#__PURE__*/_jsx(Chip, {
                    label: p.location?.city || "Virtual",
                    size: "small",
                    sx: {
                      fontSize: "0.7rem",
                      height: 22
                    }
                  }), p.price !== undefined && /*#__PURE__*/_jsx(Chip, {
                    label: p.price === 0 ? "FREE" : `${p.price} ${p.currency || "USD"}`,
                    size: "small",
                    sx: {
                      fontSize: "0.7rem",
                      height: 22,
                      fontWeight: 700
                    }
                  })]
                }), /*#__PURE__*/_jsx(Typography, {
                  variant: "subtitle1",
                  fontWeight: 600,
                  gutterBottom: true,
                  sx: {
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden"
                  },
                  children: p.header || evt.title
                }), (p.description || evt.description) && /*#__PURE__*/_jsx(Typography, {
                  variant: "body2",
                  color: "text.secondary",
                  sx: {
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    mb: 1
                  },
                  children: p.description || evt.description
                }), /*#__PURE__*/_jsx(Box, {
                  sx: {
                    mt: "auto"
                  },
                  children: /*#__PURE__*/_jsx(Button, {
                    variant: "contained",
                    size: "small",
                    sx: {
                      backgroundColor: green.primary,
                      "&:hover": {
                        backgroundColor: green.dark
                      },
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: 2
                    },
                    onClick: e => {
                      e.stopPropagation();
                      router.push(`/events/${evt.id || evt.slug}`);
                    },
                    children: "View Event"
                  })
                })]
              })]
            })
          }, evt.id);
        })
      })]
    })
  });
}

// ── component ────────────────────────────────────────────────────────────────

const HomePage = () => {
  // Fetch promoted items for each placement
  const {
    data: newsBannerPromos
  } = useGetPromotionsQuery("news_banner");
  const {
    data: featuredEventPromos
  } = useGetPromotionsQuery("featured_event");

  // Fetch all approved entities
  const {
    data: articles,
    isLoading: loadingArticles
  } = useGetArticlesQuery();
  // Promotions-first: the full events / organizations lists are fetched
  // ONLY when a promotion actually needs resolving — with no promoted
  // events there is nothing to render, so the backend is never hit.
  const {
    data: events,
    isLoading: eventsLoading,
    isError: eventsError
  } = useGetEventsQuery(undefined, {
    skip: !featuredEventPromos || featuredEventPromos.length === 0
  });
  // Same promotions query the featured-orgs section subscribes to — RTK
  // dedupes to a single network request.
  const {
    data: featuredOrgPromos
  } = useGetPromotionsQuery("featured_organization");
  const {
    data: organizations
  } = useGetOrganizationsQuery(undefined, {
    skip: !featuredOrgPromos || featuredOrgPromos.length === 0
  });
  const {
    data: regulations,
    isLoading: loadingRegulations
  } = useGetRegulationsQuery();
  const {
    settings: siteSettings
  } = useSiteSettings();

  // ── Derive news carousel items (articles + regulations combined) ──
  const newsItems = useMemo(() => {
    if (!newsBannerPromos || newsBannerPromos.length === 0) return [];
    const articleMap = new Map((articles || []).map(a => [a.id, a]));
    const regMap = new Map((regulations || []).map(r => [r.id, r]));
    return newsBannerPromos.map(promo => {
      if (promo.targetType === "regulation") {
        if (!siteSettings.regulationsVisible) return null;
        const reg = regMap.get(promo.targetId);
        if (!reg) return null;
        return regulationToNewsItem(reg, promo.imageUrl);
      }
      if (!siteSettings.articlesVisible) return null;
      const art = articleMap.get(promo.targetId);
      if (!art) return null;
      return articleToNewsItem(art, promo.imageUrl);
    }).filter(Boolean);
  }, [articles, regulations, newsBannerPromos, siteSettings.regulationsVisible, siteSettings.articlesVisible]);

  // ── Latest 10 industry updates (articles + regulations) ──
  const latestUpdates = useMemo(() => {
    const items = [];
    if (siteSettings.articlesVisible) {
      (articles || []).forEach(a => items.push({
        type: "article",
        data: a,
        date: Date.parse(a.approvedAt || a.createdAt)
      }));
    }
    if (siteSettings.regulationsVisible) {
      (regulations || []).forEach(r => items.push({
        type: "regulation",
        data: r,
        date: Date.parse(r.approvedAt || r.createdAt)
      }));
    }
    return items.sort((a, b) => b.date - a.date).slice(0, 10);
  }, [articles, regulations, siteSettings.articlesVisible, siteSettings.regulationsVisible]);

  // ── Featured events (only promoted, non-past) ──
  const featuredEvts = useMemo(() => {
    if (!events || !featuredEventPromos || featuredEventPromos.length === 0) return [];
    const evtMap = new Map(events.map(e => [e.id, e]));
    return featuredEventPromos.map(promo => evtMap.get(promo.targetId)).filter(evt => !!evt && !isEventPast(evt.payload || {}));
  }, [events, featuredEventPromos]);

  // ── Upcoming events (next 4 weeks) ──
  const upcomingEvents = useMemo(() => {
    if (!events) return [];
    return events.filter(isUpcomingSoon).sort((a, b) => {
      const da = a.payload?.date || a.createdAt;
      const db = b.payload?.date || b.createdAt;
      return Date.parse(da) - Date.parse(db);
    });
  }, [events]);

  // Each configurable homepage section, keyed for admin-driven ordering.
  // "news" bundles the carousel + latest-updates scroller; "featuredEvents"
  // bundles featured + upcoming events.
  const homepageSectionMap = {
    news: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(NewsSection, {
        newsItems: newsItems
      }), /*#__PURE__*/_jsx(LatestUpdatesSection, {
        latestUpdates: latestUpdates,
        loading: loadingArticles && loadingRegulations,
        hasNews: newsItems.length > 0
      })]
    }),
    services: /*#__PURE__*/_jsx(ServicesSection, {
      hasNews: newsItems.length > 0
    }),
    featuredOrgs: /*#__PURE__*/_jsx(FeaturedOrgsSection, {
      organizations: organizations || EMPTY_ORGS
    }),
    featuredEvents: /*#__PURE__*/_jsxs(_Fragment, {
      children: [siteSettings.eventsVisible && /*#__PURE__*/_jsx(FeaturedEventsSection, {
        featuredEvts: featuredEvts
        // Only wait on the (slow) full events list when there
        // is actually a promotion to resolve. With no active
        // promotions the section has nothing to show, so it
        // hides immediately instead of holding the skeleton
        // for the whole events fetch.
        ,
        loading:
        // Promotions are checked FIRST: while they're
        // unknown or resolved empty the section renders
        // nothing at all — the skeleton only holds when
        // a promotion exists and its event is loading.
        // A failed events fetch (incl. the baseQuery
        // hung-request timeout) settles to hidden, never
        // to a stuck skeleton.
        !!featuredEventPromos && featuredEventPromos.length > 0 && eventsLoading && !eventsError
      }), siteSettings.eventsVisible && /*#__PURE__*/_jsx(UpcomingEventsSection, {
        upcomingEvents: upcomingEvents
      })]
    }),
    featuredProfessionals: siteSettings.professionalsVisible ? /*#__PURE__*/_jsx(FeaturedProfilesHomeSection, {}) : null
  };
  return /*#__PURE__*/_jsxs(PageWrapper, {
    children: [/*#__PURE__*/_jsxs(Head, {
      children: [/*#__PURE__*/_jsx("title", {
        children: "ProdCert \u2014 Product Certification Hub"
      }), /*#__PURE__*/_jsx("meta", {
        name: "description",
        content: "Stay ahead of global compliance news, regulations, and certification events."
      })]
    }), /*#__PURE__*/_jsx(NavBar, {}), siteSettings.homepageSectionOrder.filter(key => !siteSettings.homepageHiddenSections.includes(key)).map(key =>
    /*#__PURE__*/
    // content-visibility:auto was removed here: its fixed
    // 600×600 intrinsic size did not match the real section
    // heights (~300–380px), so off-screen sections reserved
    // 600px, collapsed to real height on-screen, shortened
    // the page, re-entered the viewport, and re-expanded —
    // a frame-rate oscillation that read as strobing.
    _jsx(Box, {
      children: homepageSectionMap[key]
    }, key)), /*#__PURE__*/_jsx(SubscriptionBanner, {}), /*#__PURE__*/_jsx(SectionFooter, {})]
  });
};
export default HomePage;